import { useState } from 'react';
import { Plus } from 'lucide-react';
import { useAuth, can } from '@/hooks/useAuth';
import { Modal } from '@/components/ui/Modal';
import { CreateMerchantForm } from '@/features/merchants/CreateMerchantForm';
import { CreateCourierForm } from '@/features/couriers/CreateCourierForm';
import { WarehouseForm } from '@/features/merchants/tabs/WarehouseForm';

// Small inline "＋ New …" link shown under a Create-Order dropdown.
// All three are admin/internal only (never merchants) and each is gated by the
// caller's own module permission, so an ops user only sees what they may create.

const linkCls = 'mt-1 text-xs font-medium text-primary hover:underline inline-flex items-center gap-0.5 disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed';

export function QuickCreateMerchant({ onCreated }: { onCreated: (m: any) => void }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (user?.kind === 'merchant' || !can(user, 'Merchants', 'All Merchants', 'add')) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={linkCls}>
        <Plus size={12} /> New Merchant
      </button>
      <Modal isOpen={open} onClose={() => setOpen(false)} title="Create Merchant" subtitle="Add a merchant without leaving this order" maxWidth="2xl">
        <CreateMerchantForm onSuccess={(created) => { setOpen(false); if (created?.id) onCreated(created); }} />
      </Modal>
    </>
  );
}

export function QuickCreateCourier({ onCreated }: { onCreated: (c: any) => void }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (user?.kind === 'merchant' || !can(user, 'Couriers', 'All', 'add')) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={linkCls}>
        <Plus size={12} /> New Courier
      </button>
      <Modal isOpen={open} onClose={() => setOpen(false)} title="Create Courier" subtitle="Add a courier without leaving this order" maxWidth="2xl">
        <CreateCourierForm onSuccess={(created) => { setOpen(false); if (created?.id) onCreated(created); }} />
      </Modal>
    </>
  );
}

export function QuickCreatePickup({ merchantId, onCreated }: { merchantId?: number | ''; onCreated: (w: any) => void }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (user?.kind === 'merchant' || !can(user, 'Warehouses', 'Warehouses', 'add')) return null;
  const mid = merchantId ? Number(merchantId) : undefined;
  return (
    <>
      <button type="button" onClick={() => mid && setOpen(true)} disabled={!mid} className={linkCls}
        title={mid ? 'Add a pickup location for this merchant' : 'Select a merchant first'}>
        <Plus size={12} /> New Pickup{!mid && ' (select merchant first)'}
      </button>
      {mid && (
        <Modal isOpen={open} onClose={() => setOpen(false)} title="Create Pickup Location" subtitle="Add a pickup warehouse for the selected merchant" maxWidth="3xl">
          <WarehouseForm merchantId={mid} onSuccess={(created) => { setOpen(false); if (created?.id) onCreated(created); }} />
        </Modal>
      )}
    </>
  );
}
